import { HTMLAttributes, useState } from 'react';
import Dialog from '@/dashboard/components/Dialog/index';
import AddErnView from '@/dashboard/components/Products/policyCard/dialogs/addErn';
import CanclePausePolicyView from '@/dashboard/components/Products/policyCard/dialogs/cancelPause/index';
import ExtendPausePolicyView from '@/dashboard/components/Products/policyCard/dialogs/extendPause/index';
import PausePolicyView from '@/dashboard/components/Products/policyCard/dialogs/pausePolicy/index';
import { PausePolicyHandleType } from '@/dashboard/components/Products/policyCard/dialogs/pausePolicy/types';
import UnPausePolicyView from '@/dashboard/components/Products/policyCard/dialogs/unPausePolicy';
import { useMaterialMedia } from '@/dashboard/hooks/useMaterialMedia';
import { useProjectNavigate } from '@/dashboard/hooks/useProjectNavigate';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { usePolicyDetailStore } from '@/dashboard/store/PolicyDetailStore';
import { usePolicyStore } from '@/dashboard/store/PolicyStore';
import { PolicyHandleSourceReNew, PolicyHandleSourceUpdate } from '@/dashboard/types/PolicyTypes';
import { getRenewPolicyPath, getUpdatePolicyPath } from '@/dashboard/utils/routes';
import { InformationCircleIcon } from '@heroicons/react/outline';
import {
  ClockIcon,
  ExclamationCircleIcon,
  GiftIcon,
  OfficeBuildingIcon,
  PauseIcon,
  PlayIcon,
  RefreshIcon,
  XCircleIcon,
} from '@heroicons/react/solid';
import { JoveGridItem } from '@jove-frontend/mui';
import { ContractType, PolicyState, apiGetThePolicyInfoOfRealOperation } from '@jove-frontend/uikit';
import { Pending, PlayCircle } from '@mui/icons-material';
import { Box, Button, Grid, Typography } from '@mui/material';
import clsx from 'clsx';
import InsuranceTypeNameView from './components/insuranceTypeName';

type PolicyCardHeaderProps = HTMLAttributes<HTMLDivElement> & {
  policy: PolicyModel;
  switchToAnnual: () => void;
  emitLoading?: (loading: boolean) => void;
};

const PolicyCardHeader = (props: PolicyCardHeaderProps) => {
  const { policy, switchToAnnual, className, emitLoading } = props;
  const { isMobile } = useMaterialMedia();
  const { insuranceProviders } = usePolicyStore(({ state }) => state);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [showUnPauseDialog, setShowUnPauseDialog] = useState(false);
  const [showExtendPauseDialog, setShowExtendPauseDialog] = useState(false);
  const [showAddErnDialog, setShowAddErnDialog] = useState(false);
  const [showCancelPauseDialog, setShowCancelPauseDialog] = useState(false);
  const [pausePolicyHandleType, setPausePolicyHandleType] = useState<PausePolicyHandleType>('pause');
  const [{ cleanAllPolicyDetailInfo }] = usePolicyDetailStore((store) => [store.actions]);
  const { push } = useProjectNavigate();

  /**
   * requests
   */
  const requestRealOperationPolicyInfo = async (): Promise<any> => {
    emitLoading?.(true);
    const [res] = await apiGetThePolicyInfoOfRealOperation({
      policyId: policy?.id,
      handleType: 'renew',
    });
    emitLoading?.(false);

    return res;
  };

  /**
   * methods
   */
  // click unpause
  const unPauseAction = () => {
    setShowUnPauseDialog(true);
  };

  // click pause
  const pauseAction = () => {
    setPausePolicyHandleType('pause');
    setShowPauseDialog(true);
  };

  // click extend
  const extendPauseAction = () => {
    setShowExtendPauseDialog(true);
  };

  // click cancel pause
  const cancelPauseAction = () => {
    setShowCancelPauseDialog(true);
  };

  // click edit pause
  const editPauseAction = () => {
    setPausePolicyHandleType('edit');
    setShowPauseDialog(true);
  };

  // renew policy
  const renewAction = async () => {
    // else if (policy.isAccelerant) {
    //   router.push(
    //     getRenewPolicyPath(policy.id, {
    //       newDetail: true,
    //       detailSource: PolicyHandleSourceReNew,
    //     })
    //   );
    // }
    if (policy.isChubb) {
      cleanAllPolicyDetailInfo();
      push({ path: getRenewPolicyPath(policy?.id) });
    } else {
      const res = await requestRealOperationPolicyInfo();

      if (res) {
        push({
          path: getRenewPolicyPath(res.id, {
            newDetail: true,
            detailSource: PolicyHandleSourceReNew,
          }),
        });
      }
    }
  };

  // add ern
  const addErnAction = () => {
    setShowAddErnDialog(true);
  };

  // switch to select annual
  const switchToAnnualAction = () => {
    if (switchToAnnual) switchToAnnual();
  };

  const updatePolicyAction = () => {
    if (policy.isChubb) {
      cleanAllPolicyDetailInfo();
      push({ path: getUpdatePolicyPath(policy?.id) });
    } else {
      const params = {
        newDetail: true,
        detailSource: PolicyHandleSourceUpdate,
      };
      push({ path: getUpdatePolicyPath(policy?.id, params) });
    }
  };

  /**
   * Views
   */
  const isElapsed = policy.state === PolicyState.POLICY_ELAPSED;
  const policyStatusView = (
    <Box className="flex flex-row flex-wrap items-center gap-2">
      {!isElapsed && policy.isActive && (
        <Box className="flex h-8 flex-row items-center space-x-1 rounded-lg bg-[#D5F3E2] px-2 text-[#1CB258]">
          <PlayCircle className="h-5 w-5" />
          <Typography className="font-bold" variant="subtitle2">
            Active
          </Typography>
        </Box>
      )}
      {!isElapsed && policy.isPending && (
        <Box className="flex h-8 flex-row items-center space-x-1 rounded-lg bg-[#FCF9E7] px-2 text-[#E6C30C]">
          <Pending className="h-5 w-5" />
          <Typography className="font-bold" variant="subtitle2">
            Pending
          </Typography>
        </Box>
      )}
      {!isElapsed && policy.isPaused && (
        <Box className="flex h-8 flex-row items-center space-x-1 rounded-lg bg-[#E7F6FD] px-2 text-[#2E99C7]">
          <PauseIcon className="h-5 w-5" />
          <Typography className="font-bold" variant="subtitle2">
            Paused
          </Typography>
        </Box>
      )}
      {!isElapsed && policy.isToBePaused && (
        <Box className="flex h-8 flex-row items-center space-x-1 rounded-lg bg-[#E7F6FD] px-2 text-[#2E99C7]">
          <PauseIcon className="h-5 w-5" />
          <Typography className="font-bold" variant="subtitle2">
            To Pause {policy.pauseStartDateText}
          </Typography>
        </Box>
      )}
      {!isElapsed && policy.isExpiredSoon && (
        <Box className="flex h-8 flex-row items-center space-x-1 rounded-lg bg-[#FFE8DA] px-2 text-[#E76F2B]">
          <ExclamationCircleIcon className="h-5 w-5" />
          <Typography className="font-bold" variant="subtitle2">
            Expiring soon
          </Typography>
        </Box>
      )}
      {!isElapsed && policy.isWeeksExtended && (
        <Box className="text-brand-primary flex h-8 flex-row items-center space-x-1 rounded-lg bg-[#DBE1F8] px-2">
          <GiftIcon className="h-5 w-5" />
          <Typography className="font-bold" variant="subtitle2">
            2 weeks extended
          </Typography>
        </Box>
      )}
      {!isElapsed && policy.isExpired && (
        <Box className="flex h-8 flex-row items-center space-x-1 rounded-lg bg-[#F1F1F1] px-2 text-[#707070]">
          <ClockIcon className="h-5 w-5" />
          <Typography className="font-bold" variant="subtitle2">
            Expired
          </Typography>
        </Box>
      )}
      {!isElapsed && policy.isToBeCancel && (
        <Box className="flex h-8 flex-row items-center space-x-1 rounded-lg bg-[#FCE9E9] px-2 text-[#DD1F1F]">
          <XCircleIcon className="h-5 w-5" />
          <Typography className="font-bold" variant="subtitle2">
            To cancel {policy.canceledDateText}
          </Typography>
        </Box>
      )}
      {!isElapsed && policy.isCanceled && (
        <Box className="flex h-8 flex-row items-center space-x-1 rounded-lg bg-[#FCE9E9] px-2 text-[#DD1F1F]">
          <XCircleIcon className="h-5 w-5" />
          <Typography className="font-bold" variant="subtitle2">
            Cancelled
          </Typography>
        </Box>
      )}
      {isElapsed && (
        <Box className="flex h-8 flex-row items-center space-x-2 rounded-lg bg-[#F1F1F1] px-2 text-[#707070]">
          {/* <ClockIcon className="h-5 w-5" /> */}
          <Typography className="font-bold" variant="subtitle2">
            Archived
          </Typography>
        </Box>
      )}
    </Box>
  );

  const buttonsView = isElapsed ? null : (
    <Box className="flex flex-row flex-wrap items-center justify-end">
      {policy.canPause && (
        <Button
          className="text-brand-primary ml-1 text-sm font-bold"
          variant="text"
          startIcon={<PauseIcon className="h-5 w-5" />}
          onClick={pauseAction}
        >
          Pause policy
        </Button>
      )}
      {!policy.canRenew && policy.canUpdate && (
        <Button
          className="text-brand-primary ml-1 text-sm font-bold"
          variant="text"
          startIcon={<RefreshIcon className="h-5 w-5" />}
          onClick={updatePolicyAction}
        >
          Update policy
        </Button>
      )}
      {/* #TODO: features temporary omited for further tests after production deployment
      
      {policy.canUnpause && (
        <Button className="text-brand-primary ml-1 text-sm font-bold" variant="text" startIcon={<PlayIcon className="h-5 w-5" />} onClick={unPauseAction}>
          Unpause
        </Button>
      )}
      {policy.canExtendPause && (
        <Button className="text-brand-primary ml-1 text-sm font-bold" variant="text" onClick={extendPauseAction}>
          Extend pause
        </Button>
      )}
       */}
      {policy.canRenew && !policy.renewalDraftQuotePayload && (
        <Button
          className="text-brand-primary ml-1 text-sm font-bold"
          variant="text"
          startIcon={<RefreshIcon className="h-5 w-5" />}
          onClick={renewAction}
        >
          Update policy renew
        </Button>
      )}
      {policy.canSwitchToAnnual && (
        <Button
          style={{
            background: 'linear-gradient(91.68deg, #0B16A1 1.76%, #0B16A1 1.77%, #BB33D5 95.55%)',
          }}
          className="ml-1 w-[160px] rounded-lg text-sm font-bold text-white"
          variant="text"
          startIcon={<PlayIcon className="h-5 w-5" />}
          onClick={switchToAnnualAction}
        >
          Switch to annual
        </Button>
      )}
      {/* #TODO: features temporary omited for further tests after production deployment
      {policy.canCancelPause && (
        <Button className="text-brand-primary ml-1 text-sm font-bold" variant="text" onClick={cancelPauseAction}>
          Cancel pause
        </Button>
      )}
      {policy.canEditPause && (
        <Button className="text-brand-primary ml-1 text-sm font-bold" variant="text" onClick={editPauseAction}>
          Edit pause
        </Button>
      )}
      */}
    </Box>
  );

  return (
    <Box className={clsx('relative flex flex-col', className)}>
      <Grid container>
        {isMobile && (
          <Grid item xs={12}>
            <JoveGridItem className="mb-2 flex flex-row items-center justify-between">
              {policyStatusView}
              {buttonsView}
            </JoveGridItem>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <JoveGridItem className=" flex items-center">
            <Box className="bg-brand-secondary/10 mr-2 flex-shrink-0 rounded-full border p-3">
              <OfficeBuildingIcon className="text-brand-secondary h-7" />
            </Box>
            <Box>
              <InsuranceTypeNameView policy={policy} />
              <Box className="flex items-center space-x-2 font-bold leading-tight">
                <Typography className="font-bold" variant="body2">
                  via {insuranceProviders?.find((provider) => provider.code === policy?.providerCode)?.name}
                </Typography>
                {policy.isChubb && policy.contractType === ContractType.MONTHLY && (
                  <>
                    <span className="bg-brand-primary h-[6px] w-[6px] rounded-full border"></span>
                    <Typography variant="body2" className="text-brand-primary font-bold">
                      Contract specific
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </JoveGridItem>
        </Grid>
        {!isMobile && (
          <Grid item xs={6}>
            <JoveGridItem className="flex flex-row items-center justify-end">
              {policyStatusView}
              {buttonsView}
            </JoveGridItem>
          </Grid>
        )}
      </Grid>
      {policy.contractType === ContractType.ANNUAL && policy.isErnPending && (
        <Box className="mt-3 flex h-5 flex-row items-center">
          <InformationCircleIcon className="text-brand-primary h-5 w-5" />
          <Typography
            className={clsx('ml-2 font-medium leading-5', {
              'flex-1': isMobile,
            })}
            variant="caption"
          >
            Provide ERN within 30 days, otherwise your cover will be cancelled.
          </Typography>
          <Button className="text-brand-primary text-xs" variant="text" onClick={addErnAction}>
            {!!policy?.payload?.['ern'] ? 'Update' : 'Add'} ERN
          </Button>
        </Box>
      )}
      {/* pause dialog */}
      {showPauseDialog && (
        <Dialog close={() => setShowPauseDialog(false)} width="630px">
          <PausePolicyView className="mt-5" policy={policy} close={() => setShowPauseDialog(false)} pauseType={pausePolicyHandleType} />
        </Dialog>
      )}
      {/* unpause dialog */}
      {showUnPauseDialog && (
        <Dialog close={() => setShowUnPauseDialog(false)} width="540px">
          <UnPausePolicyView className="mt-5" policy={policy} close={() => setShowUnPauseDialog(false)} />
        </Dialog>
      )}
      {/* extend pause dialog */}
      {showExtendPauseDialog && (
        <Dialog close={() => setShowExtendPauseDialog(false)} width="630px">
          <ExtendPausePolicyView className="mt-5" policy={policy} close={() => setShowExtendPauseDialog(false)} />
        </Dialog>
      )}
      {/* add ern dialog */}
      {showAddErnDialog && (
        <Dialog close={() => setShowAddErnDialog(false)} width="560px">
          <AddErnView className="mt-5" policy={policy} close={() => setShowAddErnDialog(false)} />
        </Dialog>
      )}
      {/* cancel pause dialog */}
      {showCancelPauseDialog && (
        <Dialog close={() => setShowCancelPauseDialog(false)} width="560px">
          <CanclePausePolicyView className="mt-5" policy={policy} close={() => setShowCancelPauseDialog(false)} />
        </Dialog>
      )}
    </Box>
  );
};

export default PolicyCardHeader;
